import { Injectable, signal } from "@angular/core";
import { doc, Firestore, getDoc } from "@angular/fire/firestore";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { BehaviorSubject, Observable, Subscription, throwError } from "rxjs";
import { catchError, delay, map, shareReplay, switchMap, take } from "rxjs/operators";
import { AchievementCelebrationComponent } from "../components/achievement-celebration/achievement-celebration.component";
import { VipStatusComponent } from "../components/vip-status/vip-status.component";
import { RapidFireAnimationComponent } from "../pages/games-feed-sample/rapid-fire-animation/rapid-fire-animation.component";
import { GamesRepositoryService } from "../pages/games/games-repository.service";
import { HeroRankAnimationComponent } from "../pages/tabs/social/hero-rank-animation/hero-rank-animation.component";
import { JackpotTriggersService } from "../services/jackpot-triggers.service";
import { CatchErrorService } from "./catch-error.service";
import { GameCelebrationService } from "../pages/games/services/game-celebration.service";
const zero = 0;
@Injectable({
  providedIn: "root",
})
export class CelebrationAnimationService {
  restrictedPage: Array<string>;
  customCheck: boolean;
  allowAchievementAnimation: boolean;
  private gamePlayStatus = new BehaviorSubject<boolean>(false);
  private creditTopupStatus = new BehaviorSubject<boolean>(false);
  private xpAnimationStop: boolean;
  private celebrationTrackingSub?: Subscription;
  private vipStatusPipelineSub?: Subscription;
  private rankDetailsSub?: Subscription;
  private xpAnimationStopSub?: Subscription;
  private achievementCardDataSub?: Subscription;
  private trackCelebrationsTimeout?: ReturnType<typeof setTimeout>;
  private vipDismissUnlockTimer?: ReturnType<typeof setTimeout>;
  /** Set when user taps OK — blocks reopen until processed_status is true. */
  private vipCelebrationDismissed = false;
  goalsData: Data = [];
  badgesData: Data = [];
  achievementData: Data = [];
  isXpStatus = signal<boolean>(false);
  constructor(
    private firestore: Firestore,
    private matDialog: MatDialog,
    private gameRepoService: GamesRepositoryService,
    private router: Router,
    private catchService: CatchErrorService,
    private bottomSheet: MatBottomSheet,
    private jackpotTriggerService: JackpotTriggersService,
    private gameCelebrationService: GameCelebrationService
  ) {
    this.xpAnimationStop = false;
    this.customCheck = true;
    this.checkAllowedPagesForAnimation();
  }

  /** True when a celebration dialog/sheet is already visible — blocks stacked opens. */
  private isCelebrationUiOpen(): boolean {
    const openDialog = (this.matDialog.openDialogs || []).some((ref) => {
      const instance = ref?.componentInstance;
      return (
        instance instanceof VipStatusComponent ||
        instance instanceof HeroRankAnimationComponent ||
        instance instanceof RapidFireAnimationComponent
      );
    });
    return openDialog || !this.customCheck;
  }

  /**
   * Called from VipStatus OK — keep the pipeline locked so a late
   * current_celebration snapshot (processed_status still false) cannot reopen.
   */
  markVipCelebrationDismissed(): void {
    this.vipCelebrationDismissed = true;
    this.customCheck = false;
    if (this.vipDismissUnlockTimer != null) {
      clearTimeout(this.vipDismissUnlockTimer);
    }
    // Safety unlock if Firestore update never arrives.
    this.vipDismissUnlockTimer = setTimeout(() => {
      this.vipDismissUnlockTimer = null;
      if (this.vipCelebrationDismissed) {
        this.vipCelebrationDismissed = false;
        this.customCheck = true;
      }
    }, 15000);
  }

  private clearVipCelebrationDismissed(): void {
    if (this.vipDismissUnlockTimer != null) {
      clearTimeout(this.vipDismissUnlockTimer);
      this.vipDismissUnlockTimer = null;
    }
    this.vipCelebrationDismissed = false;
  }

  // This method has been used to check allowed pages for  display animation
  public checkAllowedPagesForAnimation(): void {
    this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          return (
            isOnline &&
            getDoc(doc(this.firestore, `/Global_lookup/animation_allowed_pages`))
              .then((snapshot) => ({ payload: snapshot }))
              .catch((err) => {
                return err;
              })
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(map((res: any) => res?.payload?.data()))
      .pipe(take(1), delay(3000))
      .subscribe(
        (l: any) => {
          if (!l || !l?.list || l?.list?.length == 0) return;
          this.restrictedPage = l?.list ? l?.list.map((d) => "/mainTabs/tabs/" + d) : [];
        },
        (err) => {
          return err;
        }
      );
  }
  // This method has been used to track event the celebration animation
  trackCelebrations() {
    this.allowAchievementAnimation = true;
    // Collapse rapid re-entries (ionViewWillEnter / claim / navigation) into one listener.
    if (this.trackCelebrationsTimeout) {
      clearTimeout(this.trackCelebrationsTimeout);
    }
    this.trackCelebrationsTimeout = setTimeout(() => {
      this.celebrationTrackingSub?.unsubscribe();
      this.celebrationTrackingSub = this.gameCelebrationService
        ?.getCurrentCelebrationData()
        .pipe(
          // Handling the error before subscribing to the data
          catchError((error) => {
            return throwError(() => error);
          })
        )
        .subscribe((res: Data) => {
          const currentCelebration = res?.type;
          const processedStatus = res?.processed_status;
          this.getAchievementCardData();

          // Handling the error
          try {
            // Firestore caught up after OK — safe to unlock for the next celebration.
            if (processedStatus === true) {
              this.clearVipCelebrationDismissed();
              this.customCheck = true;
              return;
            }
            // Skip while a celebration is already open/in-flight (prevents flicker / multi-open).
            if (
              this.vipCelebrationDismissed ||
              this.isCelebrationUiOpen()
            ) {
              return;
            }
            // this switch statement is used to call the animation script
            const currentGamePlayStatus = this.gamePlayStatus?.value;
            const creditTopupStatus = this.creditTopupStatus?.value;
            if (
              !currentGamePlayStatus &&
              !creditTopupStatus &&
              this.restrictedPage?.some((d) => this.router.url.includes(d))
            ) {
              switch (currentCelebration) {
                case "gem_wallet":
                  this.gemWalletAnimation(currentCelebration, processedStatus);
                  break;
                case "vip_level_up":
                  this.getRankData("vxp_level");
                  break;
                case "community_level_up":
                  this.getRankData("cxp_level");
                  break;
                case "gaming_level_up":
                  this.getRankData("gxp_level");
                  break;
                case "fortune_level_up":
                  this.getRankData("fxp_level");
                  break;
                case "vip_status_level_up":
                  this.vipStatusLevelUpAnimation(currentCelebration, processedStatus);
                  break;
                case "achievement":
                  if (this.allowAchievementAnimation) {
                    this.allowAchievementAnimation = false;
                    this.achievementAnimation();
                  }
                  break;
                default:
                  // In default case it will be empty
                  break;
              }
            }
            // Call the Animation Script according to condition
          } catch (error) {
            // Handling the error
            return error;
          }
        });
    }, 1000);
  }
  // Gem_wallet Animation Script
  gemWalletAnimation(currentCelebration: string, processedStatus: boolean) {
    this.xpAnimationStopSub?.unsubscribe();
    this.xpAnimationStopSub = this.jackpotTriggerService.xpAnimationStop$
      .pipe(take(1))
      .subscribe((xpAnimationStop) => {
        this.xpAnimationStop = xpAnimationStop ?? false;
      });
    // This method has been used to get current celebration data
    // Call the Animation Script according to condition
    if (
      currentCelebration === "gem_wallet" &&
      processedStatus === false &&
      this.customCheck &&
      !this.xpAnimationStop &&
      !this.isCelebrationUiOpen()
    ) {
      this.customCheck = false;
      const dialog = this.matDialog.open(RapidFireAnimationComponent, {
        height: "100%",
        width: "100%",
        backdropClass: "xpsAnimationBackdrop",
        panelClass: "xps-animation",
      });
      dialog.afterClosed().subscribe(() => {
        this.customCheck = true;
        this.jackpotTriggerService.setXpAnimationStop(false);
        // Handling the XP animation
        this.isXpStatus.set(true);
      });
    }
  }
  // ================================ Rank Celebration Animation Script================================//
  // This function is used to get the rank data
  getRankData(rankLevel: string) {
    if (this.isCelebrationUiOpen()) {
      return;
    }
    this.rankDetailsSub?.unsubscribe();
    this.rankDetailsSub = this.gameCelebrationService
      .getRankDetailsData()
      .pipe(
        take(1),
        // Handling the error before subscribing to the data
        catchError((error) => {
          return throwError(() => error);
        })
      )
      .subscribe((res: Data) => {
        const rankCelebrationData = res;
        const currentRankLevelData = [];
        // This variable contain current rank data
        rankCelebrationData.filter((data: Data) => {
          const rank_Level = data?.data?.celebration_rank?.requester;
          const current_Level = rank_Level.replace(/[""]+/g, "");
          if (current_Level === rankLevel) {
            currentRankLevelData.push(data);
          }
        });
        // This method has been used to
        if (currentRankLevelData[zero] !== null && currentRankLevelData[zero] !== undefined) {
          this.heroAnimation(currentRankLevelData[zero]);
        } else {
          return throwError("No Rank Found");
        }
      });
  }
  // This method has been used to open the hero animation
  heroAnimation(currentRankLevelData: Data) {
    if (
      currentRankLevelData !== undefined &&
      currentRankLevelData !== null &&
      this.customCheck &&
      !this.isCelebrationUiOpen()
    ) {
      this.customCheck = false;
      const dialog = this.matDialog.open(HeroRankAnimationComponent, {
        height: "100%",
        width: "100%",
        panelClass: "hero-rank-animation",
        data: currentRankLevelData,
      });
      dialog.afterClosed().subscribe(() => {
        this.customCheck = true;
      });
    }
  }
  // This method has been used get RankLevels Data from the Global Collection
  globalGamificationRanksData(userLevels: number, docRef: string) {
    if (userLevels !== null && userLevels !== undefined && docRef !== null && docRef !== undefined) {
      return this.catchService.isOnline$
        .pipe(
          switchMap((isOnline) => {
            if (isOnline) {
              const docRefPath = doc(
                this.firestore,
                `gamification_ranks/${docRef}/level/${userLevels}`
              );
              return getDoc(docRefPath).then((snapshot) => ({
                data: snapshot.data(),
              }));
            }
            return null;
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        )
        .pipe(
          map((res: any) => {
            const data = res?.data;
            return data;
          })
        );
    }
  }

  // VIP STATUS LEVEL UP Script
  vipStatusLevelUpAnimation(currentCelebration: string, processedStatus: boolean) {
    // This method has been used to get current celebration data
    if (
      currentCelebration !== "vip_status_level_up" ||
      processedStatus !== false ||
      this.isCelebrationUiOpen()
    ) {
      return;
    }

    // Lock immediately so parallel trackCelebrations / snapshot races cannot open twice.
    this.customCheck = false;
    this.vipStatusPipelineSub?.unsubscribe();
    this.vipStatusPipelineSub = this.gameCelebrationService
      .getVipStatusDetailsData()
      .pipe(
        take(1),
        // Handling the error before subscribing to the data
        catchError((error) => {
          this.customCheck = true;
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res: Data) => {
          // Handling the error before subscribing to the data
          try {
            const vipStatusData = res?.[zero];
            const vipStatusRank = vipStatusData?.id;
            const celebrationRank = vipStatusData?.data?.celebration_rank?.current_rank;
            const presentedStatus = vipStatusData?.data?.presented;
            // This condition is used to check the processed status
            if (presentedStatus === false && vipStatusRank != null && celebrationRank != null) {
              // One-shot lookup — avoid re-opening on later Global_lookup snapshot emits.
              this.gameCelebrationService
                .getVipStatusLevelUpData()
                .pipe(take(1))
                .subscribe({
                  next: (levelUpRes: Data) => {
                    const vipStatusLevelUpData = levelUpRes?.[celebrationRank];
                    this.vipCardAnimationCode(vipStatusLevelUpData, vipStatusRank);
                  },
                  error: () => {
                    this.customCheck = true;
                  },
                });
            } else {
              this.customCheck = true;
              // Handled Errors if processed status is not defined
              return throwError("Processed status is not defined");
            }
          } catch (err) {
            this.customCheck = true;
            // Handled Errors if vip status data is not defined
            return throwError(err);
          }
        },
        error: () => {
          this.customCheck = true;
        },
      });
  }
  // This is used for the display vip card animation
  vipCardAnimationCode(vipCelebration: Data, vipStatusRank: string) {
    const vipDialogAlreadyOpen = (this.matDialog.openDialogs || []).some(
      (ref) => ref?.componentInstance instanceof VipStatusComponent
    );
    if (vipCelebration !== undefined && vipCelebration !== null && !vipDialogAlreadyOpen) {
      // customCheck is already false from vipStatusLevelUpAnimation (in-flight lock).
      this.customCheck = false;
      const dialog = this.matDialog.open(VipStatusComponent, {
        height: "100%",
        width: "100%",
        disableClose: true,
        data: { vipCelebration, vipStatusRank },
      });
      dialog.afterClosed().subscribe(() => {
        // Do not unlock here — markVipCelebrationDismissed (OK) or the
        // processed_status===true path owns unlock to avoid reopen flicker.
        if (!this.vipCelebrationDismissed) {
          this.markVipCelebrationDismissed();
        }
      });
    } else {
      this.customCheck = true;
      return throwError("VIP Celebration data not found");
    }
  }
  // This method has been used get VIP STATUS RankLevels Data from the Global Collection
  globalGamificationVipStatusRanksData(userLevels: number) {
    if (userLevels !== null && userLevels !== undefined) {
      return this.catchService.isOnline$
        .pipe(
          switchMap((isOnline) => {
            if (isOnline) {
              const docRefPath = doc(this.firestore, `/gamification_ranks/vip_status/level/${userLevels}`);
              return getDoc(docRefPath).then((snapshot) => ({
                data: snapshot.data(),
              }));
            }
            return null;
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        )
        .pipe(
          map((res: any) => {
            const data = res?.data;
            return data;
          })
        );
    } else {
      return throwError("No Rank Found");
    }
  }

  // This method has been used to open the achievement animation
  achievementAnimation() {
    setTimeout(() => {
      // Achievement data
      const achievementData = {
        achievementData: this.achievementData,
        goalsData: this.goalsData,
        badgesData: this.badgesData,
      };
      // Condition check Achievement card data null
      if (
        this.customCheck &&
        !this.isCelebrationUiOpen() &&
        this.achievementData != null &&
        this.achievementData?.length > 0
      ) {
        this.customCheck = false;
        const bottomSheetClose = this.bottomSheet.open(AchievementCelebrationComponent, {
          panelClass: "achievement-cel-bottomsheet",
          hasBackdrop: true,
          disableClose: true,
          data: achievementData,
        });
        bottomSheetClose.afterDismissed().subscribe(() => {
          this.customCheck = true;
        });
      } else {
        return throwError("Achievement Data not found");
      }
    }, 1000);
  }

  public setGamePlayStatus(status: boolean): void {
    this.gamePlayStatus.next(status);
  }

  getGamePlayStatus(): Observable<boolean> {
    return this.gamePlayStatus.asObservable();
  }

  public setCreditTopupStatus(status: boolean): void {
    this.creditTopupStatus.next(status);
  }

  public getCreditTopupStatus(): Observable<boolean> {
    return this.creditTopupStatus.asObservable();
  }
  // This method has been used to get achievement card data
  public getAchievementCardData() {
    // Subscribe once — trackCelebrations used to stack badge/goal listeners every call.
    if (this.achievementCardDataSub && !this.achievementCardDataSub.closed) {
      return;
    }

    const badgesSub = this.gameCelebrationService.getBadgesDetailsData$.subscribe(async (res: Data) => {
      this.badgesData = res;
      if (res?.length === 0) {
        const imagesMatch = this.achievementData?.some((item) => item.type === "badge");
        if (imagesMatch) {
          // Use filter to create a new array without elements with type 'badge'
          this.achievementData = this.achievementData?.filter((item) => item.type !== "badge");
        }
      }
      if (Array.isArray(res)) {
        await res?.forEach((doc: Data) => {
          try {
            const docID = doc?.id;
            const docRef = doc?.settings?.document_ref;
            const updateTimestamp = doc?.update_timestamp;

            this.gameCelebrationService
              .getBadgeLookupData(docRef)
              .pipe(
                take(1),
                catchError((error) => {
                  return throwError(() => error);
                })
              )
              .subscribe((res1: Data) => {
                res1.type = "badge";
                res1.viewed = false;
                res1.id = docID;
                res1["update_timestamp"] = updateTimestamp;
                res1["id"] = docID;
                if (this.achievementData?.length === 0) {
                  this.achievementData.push(res1);
                }
                const isExists = this.achievementData?.some((item) => item.id === res1?.id);

                if (!isExists) {
                  // If res1.id is not found in achievementData, push res1 into the array
                  this.achievementData.push(res1);
                }
              });
          } catch (e) {
            return throwError(e);
          }
        });
      }
    });

    // This method has been used to get Goals Current Data
    const goalsSub = this.gameCelebrationService.getGoalsCurrentData$.subscribe(async (res: Data) => {
      try {
        await res.forEach((data) => {
          if (data?.settings?.completed === true) {
            data.type = "goal";
            data.viewed = false;
            const isExists = this.achievementData?.some((item) => item.id === data?.id);
            if (!isExists) {
              {
                this.achievementData.push(data);
                this.goalsData.push(data);
              }
            }
          }
        });
      } catch (e) {
        return throwError(e);
      }
    });

    this.achievementCardDataSub = new Subscription();
    this.achievementCardDataSub.add(badgesSub);
    this.achievementCardDataSub.add(goalsSub);
  }
}
